/**
 * Lightweight polling hook for new orders / appointments.
 * - Polls every 15s.
 * - On first tick, seeds baseline (no toast).
 * - On subsequent ticks, if new "nuevo" items increased, fires toast + beep.
 * Returns { newOrdersCount, newAppointmentsCount }.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 15000;

// Programmatic short "ding" via Web Audio (no asset needed)
function playDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.08);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.4);
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch {
    /* audio blocked, silent */
  }
}

export function useLiveCounters(enabled = true) {
  const [ordersNew, setOrdersNew] = useState(0);
  const [apptsNew, setApptsNew] = useState(0);
  const [humanNew, setHumanNew] = useState(0);
  const [paymentsNew, setPaymentsNew] = useState(0);
  const prevOrders = useRef(null);
  const prevAppts = useRef(null);
  const prevHuman = useRef(null);
  const prevPayments = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;

    const tick = async () => {
      try {
        const [oResp, aResp, hResp, pResp] = await Promise.all([
          api.get("/orders", { params: { status: "nuevo" } }),
          api.get("/appointments"),
          api.get("/human-inbox"),
          api.get("/payment-proofs", { params: { status: "pending" } }),
        ]);
        if (!alive) return;
        const oCount = Array.isArray(oResp.data) ? oResp.data.length : 0;
        const aList = Array.isArray(aResp.data) ? aResp.data : [];
        const aCount = aList.filter((x) => (x.status || "").toLowerCase() === "confirmada" || x.status === "nueva").length;
        const hCount = Array.isArray(hResp.data) ? hResp.data.length : 0;
        // Only count proofs that already have an image (ready for review)
        const pList = Array.isArray(pResp.data) ? pResp.data : [];
        const pCount = pList.filter((x) => x.has_image).length;

        // Compare with previous (skip first pass)
        if (prevOrders.current !== null && oCount > prevOrders.current) {
          const diff = oCount - prevOrders.current;
          toast.success(`${diff} pedido${diff === 1 ? "" : "s"} nuevo${diff === 1 ? "" : "s"}`, {
            description: "Revisa la sección Pedidos",
          });
          playDing();
        }
        if (prevAppts.current !== null && aCount > prevAppts.current) {
          const diff = aCount - prevAppts.current;
          toast.info(`${diff} cita${diff === 1 ? "" : "s"} agendada${diff === 1 ? "" : "s"}`, {
            description: "Revisa el calendario",
          });
          playDing();
        }
        if (prevHuman.current !== null && hCount > prevHuman.current) {
          const diff = hCount - prevHuman.current;
          toast.warning(`${diff} conversacion${diff === 1 ? "" : "es"} necesita${diff === 1 ? "" : "n"} un humano`, {
            description: "Abre Bandeja humano",
          });
          playDing();
        }
        if (prevPayments.current !== null && pCount > prevPayments.current) {
          const diff = pCount - prevPayments.current;
          toast.success(`${diff} comprobante${diff === 1 ? "" : "s"} de pago para revisar`, {
            description: "Abre Comprobantes",
          });
          playDing();
        }

        prevOrders.current = oCount;
        prevAppts.current = aCount;
        prevHuman.current = hCount;
        prevPayments.current = pCount;
        setOrdersNew(oCount);
        setApptsNew(aCount);
        setHumanNew(hCount);
        setPaymentsNew(pCount);
      } catch {
        /* ignore transient errors */
      }
    };

    tick(); // initial fetch (seeds baseline)
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [enabled]);

  return { ordersNew, apptsNew, humanNew, paymentsNew };
}
